enum StudentStatus {
  UNCATEGORIZED = "Un-categorized",
  INACTIVE = "Inactive",
  INTERESTED_PARTICIPANT = "Interested",
  WITHDRAWN = "Withdrawn",
  ALUMNI = "Alumni",
  EXPERIENCE_EXPLORER = "Explorer",
  SUBMITTED = "Submitted",
  CONFIRMED = "Confirmed",
  ACTIVELY_PARTICIPATING = "Actively Participating",
}

enum StudentSource {
  APIC = "APIC",
  MARKETO = "Marketo",
}

enum StudentApprovalStatus {
  APPROVED = "Approved",
  NEEDS_APPROVAL = "Needs Approval",
}

enum StudentOnboardingStatus {
  STILL_NEEDS_WORK = "Still needs work",
  COMPLETE = "Complete",
}

export { StudentStatus, StudentSource, StudentApprovalStatus, StudentOnboardingStatus };

interface Application {
  id: string;
  home_approval_status?: string;
  host_accepted_status?: string;
  host_accepted_at?: string;
  host_rejected_at?: string;
  host_provisionally_accepted_at?: string;
  host_conditionally_accepted_at?: string;
}

interface StudentCollege {
  id: string;
  name: string;
}

interface StudentStudyArea {
  cip_code: string;
  study_area: string;
}

interface StudentProfile {
  profile_id: string;
  created_at: Date;
  source_system: string;
  source_system_internal_id?: string;
  participant_id: string;
  first_name_preferred?: string;
  first_name_legal?: string;
  last_name?: string;
  email?: string;
  user_types: string[];
  areas_of_study: StudentStudyArea[];
  approval_status: string;
  graduating_year?: string;
  using_mobile: boolean;
  status?: string;
  onboarding_status: string;
  colleges: StudentCollege[];
  applications?: Application[];
}

interface SessionLocation {
  city_id: string;
  city: string;
  country_id: string;
  country: string;
}

interface SessionDates {
  arrival_date?: Date;
  departure_date?: Date;
}

interface SessionProgram {
  id: string;
  name: string;
}

interface SessionInfo {
  academic_year?: string;
  academic_start_date?: Date[];
  academic_end_date?: Date[];
  leaders: string[];
  locations: SessionLocation[];
  session_start_date_ts: number;
  session_end_date_ts: number;
  dates: SessionDates[];
  name: string;
  channel?: string;
  session_types: string[];
  session_type_ids: string[];
  deadline?: Date[];
  terms: string[];
  requires_visa: boolean;
  session_id: string;
  requires_application?: boolean;
  programs: SessionProgram[];
}

interface EntityInfo {
  session_owner_id: string;
  room_owners: string[];
  excursion_owners: string[];
  internship_owners: string[];
}

interface OrderCore {
  id: string;
  created_at: Date;
  application_id?: string;
  host_institution_id?: string;
  host_accepted_status?: string;
  status: string;
}

interface OrderFinancialBreakdown {
  is_cleared?: boolean;
  using_financial_aid?: boolean;
  total_price_int_cents?: number;
  institution_total_price_int_cents?: number;
  student_total_price_int_cents?: number;
}

interface OrderHistory {
  status_date: string;
  order_status: string;
  updated_by?: string;
}

interface OrderDetail {
  core: OrderCore;
  financial: OrderFinancialBreakdown;
  session: SessionInfo;
  entity: EntityInfo;
  history: OrderHistory[];
}

interface StudentInfo {
  objectID: string;
  record_updated_at: string;
  record_updated_at_ts: number;
  student: StudentProfile;
}

interface AlgoliaStudent extends StudentInfo {
  orders: OrderDetail[];
}

interface AlgoliaStudentOrder extends StudentInfo {
  order: OrderDetail;
}

const DEFAULT_STUDENT_ORDER: AlgoliaStudentOrder = {
  objectID: "",
  record_updated_at: "",
  record_updated_at_ts: 0,
  student: {
    profile_id: "",
    created_at: new Date(),
    source_system: "",
    participant_id: "",
    user_types: [],
    areas_of_study: [],
    approval_status: "",
    using_mobile: false,
    onboarding_status: "",
    colleges: [],
  },
  order: {
    core: {
      id: "",
      created_at: new Date(),
      status: "",
    },
    financial: {},
    history: [],
    session: {
      leaders: [],
      locations: [],
      session_start_date_ts: 0,
      session_end_date_ts: 0,
      dates: [],
      name: "",
      session_types: [],
      session_type_ids: [],
      terms: [],
      requires_visa: false,
      session_id: "",
      programs: [],
    },
    entity: {
      session_owner_id: "",
      room_owners: [],
      excursion_owners: [],
      internship_owners: [],
    },
  },
};

export type {
  AlgoliaStudent,
  AlgoliaStudentOrder,
  StudentInfo,
  OrderDetail,
  OrderHistory,
  OrderFinancialBreakdown,
  OrderCore,
  EntityInfo,
  SessionInfo,
  SessionProgram,
  SessionDates,
  SessionLocation,
  StudentProfile,
  StudentStudyArea,
  StudentCollege,
  Application,
};

export { DEFAULT_STUDENT_ORDER };
